import React, {useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState} from "react";
import {ProgressMessage} from "../../api/Model";
import {
    Box,
    Button,
    ButtonProps,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Input,
    MenuItem,
    Select,
    Stack,
    Typography
} from "@mui/material";
import ErrorIcon from '@mui/icons-material/Error';
import WarningIcon from '@mui/icons-material/Warning';
import SuccessIcon from '@mui/icons-material/CheckCircle';
import InfoIcon from '@mui/icons-material/Info';
import StopIcon from '@mui/icons-material/Stop';
import {noop} from "../../util/FunctionUtil";
import {Lifetime} from "../../util/Lifetime";
import {collect} from "../../util/concurrency/AsyncGeneratorUtil";
import {inputChange, selectChange, SetState} from "../../util/FormHandlers";
import {useConfirm} from "material-ui-confirm";

const STICK_TO_BOTTOM_THRESHOLD = 12;

type ProgressMessageType = NonNullable<ProgressMessage['type']>;
type ProgressMessageTypeFilter = ProgressMessageType | 'all';

const PROGRESS_MESSAGE_TYPE_FILTER_OPTIONS: { value: ProgressMessageTypeFilter, label: string }[] = [
    {value: 'all', label: 'All Types'},
    {value: 'error', label: 'Errors'},
    {value: 'warning', label: 'Warnings'},
    {value: 'success', label: 'Success'},
    {value: 'info', label: 'Infos'},
];



type MessageFilter = {
    type: ProgressMessageTypeFilter;
    text: string;
}

const emptyFilter: MessageFilter = {
    type: 'all',
    text: '',
};

const filterInputSx = {
    color: 'grey.100',
    '&:before': {
        borderBottomColor: 'grey.600',
    },
    '&:after': {
        borderBottomColor: 'grey.300',
    },
    '&:hover:not(.Mui-disabled):before': {
        borderBottomColor: 'grey.400',
    },
    '& .MuiSelect-icon': {
        color: 'grey.300',
    },
};

function ProgressDialogMessageFilters({filter, setFilter}: {
    filter: MessageFilter,
    setFilter: SetState<MessageFilter>,
}) {
    const handlers = useMemo(
        () => ({
            typeFilter: selectChange(setFilter, value => ({
                type: value as ProgressMessageTypeFilter,
            })),
            textFilter: inputChange(setFilter, value => ({
                text: value,
            })),
        }),
        [setFilter],
    );

    return (
        <Stack direction="row" gap={2} sx={{flexGrow: 1}}>
            <Input
                size="small"
                fullWidth
                value={filter.text}
                onChange={handlers.textFilter}
                placeholder="Filter messages"
                sx={filterInputSx}
            />
            <Select
                labelId="progress-message-type-filter-label"
                value={filter.type}
                onChange={handlers.typeFilter}
                variant="standard"
                sx={{
                    width: '200px',
                    ...filterInputSx,
                }}
                MenuProps={{
                    slotProps: {
                        paper: {
                            sx: {
                                bgcolor: '#101214',
                                color: 'grey.100',
                                border: 1,
                                borderColor: 'grey.800',
                            },
                        }
                    },
                }}
            >
                {PROGRESS_MESSAGE_TYPE_FILTER_OPTIONS.map(option => (
                    <MenuItem key={option.value} value={option.value}>
                        {option.label}
                    </MenuItem>
                ))}
            </Select>
        </Stack>
    );
}

function ProgressMessageIcon({message}: {
    message: ProgressMessage,
}) {
    if (message.type === 'error') return <ErrorIcon color="error" fontSize="small"/>;
    if (message.type === 'success') return <SuccessIcon color="success" fontSize="small"/>;
    if (message.type === 'warning') return <WarningIcon color="warning" fontSize="small"/>;
    if (message.type === 'info') return <InfoIcon color="info" fontSize="small"/>;

    return (
        <CircularProgress
            size={18}
            variant={message.progress !== undefined ? 'determinate' : 'indeterminate'}
            value={message.progress}
            sx={{color: 'grey.300'}}
        />
    );
}

function ProgressDialogMessage({message}: {
    message: ProgressMessage,
}) {
    return (
        <Stack direction="row" gap={1.25} alignItems="flex-start">
            <Box sx={{width: 20, minWidth: 20, pt: 0.25, display: 'flex', justifyContent: 'center'}}>
                <ProgressMessageIcon message={message}/>
            </Box>
            <Typography
                variant="body2"
                component="div"
                sx={{
                    color: 'grey.100',
                    fontFamily: 'JetBrains Mono, Roboto, Monaco, Consolas, "Courier New", monospace',
                    whiteSpace: 'pre-wrap',
                    overflowWrap: 'anywhere',
                    lineHeight: 1.55,
                }}
            >
                {message.message}
            </Typography>
        </Stack>
    );
}

function filterMessages(messages: ProgressMessage[], filter: MessageFilter): ProgressMessage[] {
    let filteredMessages = messages;
    if (filter.type !== 'all') {
        filteredMessages = filteredMessages.filter(message => (message.type || 'info') === filter.type);
    }
    if (filter.text) {
        const q = filter.text.toLowerCase();
        filteredMessages = filteredMessages.filter(message => message.message.toLowerCase().includes(q));
    }
    return filteredMessages;
}

function ProgressDialogMessages({message, messageStack, filter}: {
    message: ProgressMessage,
    messageStack: ProgressMessage[],
    filter: MessageFilter,
}) {
    const scrollContainerRef = useRef<HTMLDivElement | null>(null);
    const shouldStickToBottomRef = useRef(true);
    const programmaticScrollRef = useRef(false);
    const messages = useMemo(
        () => message === messageStack[messageStack.length - 1] ? messageStack : [...messageStack, message],
        [message, messageStack]
    );
    const filteredMessages = useMemo(
        () => filterMessages(messages, filter),
        [messages, filter]
    );

    const handleScroll = useCallback(() => {
        const scrollContainer = scrollContainerRef.current;
        if (!scrollContainer) return;
        if (programmaticScrollRef.current) return;

        const distanceToBottom = scrollContainer.scrollHeight - scrollContainer.scrollTop - scrollContainer.clientHeight;
        shouldStickToBottomRef.current = distanceToBottom < STICK_TO_BOTTOM_THRESHOLD;
    }, []);

    useLayoutEffect(() => {
        const scrollContainer = scrollContainerRef.current;
        if (scrollContainer && shouldStickToBottomRef.current) {
            programmaticScrollRef.current = true;
            scrollContainer.scrollTop = scrollContainer.scrollHeight;
            const animationFrameId = requestAnimationFrame(() => {
                programmaticScrollRef.current = false;
            });

            return () => cancelAnimationFrame(animationFrameId);
        }
    }, [filteredMessages]);

    return (
        <Box
            ref={scrollContainerRef}
            onScroll={handleScroll}
            sx={{
                py: 1,
                height: '100%',
                minHeight: 0,
                overflowY: 'auto',
            }}
        >
            <Stack direction="column" gap={1}>
                {filteredMessages.map((message, index) => (
                    <ProgressDialogMessage message={message} key={index}/>
                ))}
                {filteredMessages.length === 0 && (
                    <Typography variant="body2" color="grey.500">
                        No messages match the current filters.
                    </Typography>
                )}
            </Stack>
        </Box>
    );
}

export function ProgressDialog({initialMessage, process}: {
    initialMessage: string,
    process?: {
        lifetime: Lifetime,
        progress: AsyncGenerator<ProgressMessage, void>,
    },
}) {
    const confirm = useConfirm();
    const [message, setMessage] = useState({message: initialMessage} as ProgressMessage);
    const [messageStack, setMessageStack] = useState<ProgressMessage[]>([]);
    const [closeButton, setCloseButton] = useState<ButtonProps>({
        children: 'Close',
        onClick: noop,
        color: 'primary',
    });
    const [open, setOpen] = useState(false);
    const [filter, setFilter] = useState<MessageFilter>(emptyFilter);

    const handleClose = useCallback(async () => {
        if (process?.lifetime.isAlive) {
            const {confirmed} = await confirm({
                title: 'Stop process?',
                description: 'The process is still running. Are you sure you want to stop it?',
                confirmationText: 'Stop',
                confirmationButtonProps: {
                    color: 'error',
                    variant: 'contained',
                },
            });
            if (!confirmed) return;

            process.lifetime.terminate();
        }
        setOpen(false);
    }, [confirm, process]);

    const addMessage = useCallback((message: ProgressMessage) => {
        if (message.type !== undefined) {
            setMessageStack(stack => [...stack, message]);
        }
        setMessage(message);
    }, [setMessage, setMessageStack]);

    useEffect(() => {
        if (process) {
            setOpen(true);
            setMessage({message: initialMessage});
            setMessageStack([]);
            setFilter(emptyFilter);
            setCloseButton({
                children: 'Stop',
                color: 'error',
                variant: 'outlined',
                startIcon: <StopIcon/>,
                onClick: () => {
                    process.lifetime.terminate();
                    setOpen(false);
                }
            });
            process.lifetime.onTermination(() => {
                setCloseButton({
                    children: 'Close',
                    color: 'primary',
                    onClick: () => setOpen(false),
                });
            });
            collect(process.progress, addMessage);
        }
    }, [process, initialMessage, addMessage, setMessage, setMessageStack, setOpen, setFilter, setCloseButton]);

    return (
        <Dialog
            open={open}
            onClose={handleClose}
            fullWidth
            maxWidth="lg"
            slotProps={{
                paper: {
                    sx: {
                        height: '80vh',
                        bgcolor: '#101214',
                        color: 'grey.100',
                    }
                }
            }}
        >
            <DialogTitle sx={{borderBottom: 1, borderColor: 'grey.800'}}>
                <Stack direction="row" gap={8} alignItems="center">
                    <Typography variant="inherit">
                        {initialMessage}
                    </Typography>
                    <ProgressDialogMessageFilters filter={filter} setFilter={setFilter}/>
                </Stack>
            </DialogTitle>
            <DialogContent sx={{overflow: 'hidden', py: 2}}>
                <ProgressDialogMessages
                    message={message}
                    messageStack={messageStack}
                    filter={filter}
                />
            </DialogContent>
            <DialogActions sx={{px: 3, py: 2, borderTop: 1, borderColor: 'grey.800'}}>
                <Button {...closeButton}/>
            </DialogActions>
        </Dialog>
    );
}
